import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Loader2, Eye, Download, Archive, RotateCcw } from 'lucide-react';
import { expensesApi, receiptsApi, projectsApi, peopleApi } from '../api';
import { dollarsToCents, centsToDollarInput, fmtDate } from '../constants';
import { MoneyInput } from './MoneyInput';
import { EXPENSE_TYPE_OPTIONS, PAYMENT_METHOD_OPTIONS, REIMBURSEMENT_OPTIONS, fmtBytes } from '../expenseConstants';
import { ReceiptThumb, ReceiptPreviewDialog, downloadReceipt } from './ReceiptPreviewDialog';

const inputCls = 'bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37]';
const selectCls =
  'w-full bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';

const Section = ({ title, children }) => (
  <div className="border-t border-gray-800 pt-5 first:border-t-0 first:pt-0">
    <h4 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wide mb-3">{title}</h4>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
  </div>
);

const Field = ({ label, children, full, help }) => (
  <div className={full ? 'md:col-span-2' : ''}>
    <label className="text-sm text-gray-300 mb-1.5 block font-medium">{label}</label>
    {children}
    {help && <p className="text-xs text-gray-500 mt-1">{help}</p>}
  </div>
);

const EMPTY = {
  expense_date: '', vendor_name: '', expense_type: 'materials', work_category: '',
  description: '', reference_number: '', project_id: '',
  subtotal: '', tax: '', other_fees: '', total: '',
  payment_method: '', payer_key: 'company', reimbursement_status: 'not_applicable',
  internal_notes: '',
};

export const ExpenseFormDialog = ({
  open, onOpenChange, mode = 'create', expense, lockedProjectId, onSaved,
}) => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [projects, setProjects] = useState([]);
  const [people, setPeople] = useState([]);
  const [attached, setAttached] = useState([]);
  const [preview, setPreview] = useState(null);
  const locked = lockedProjectId !== undefined && lockedProjectId !== null;

  const loadAttached = useCallback(() => {
    if (mode === 'edit' && expense) {
      receiptsApi.list({ expense_id: expense.id }).then((r) => setAttached(r.data)).catch(() => {});
    }
  }, [mode, expense]);

  useEffect(() => {
    if (!open) return;
    projectsApi.list({ archived: false, sort: 'name_az' }).then((r) => setProjects(r.data)).catch(() => {});
    peopleApi.assignees().then((r) => setPeople(r.data)).catch(() => {});
    setPreview(null);
    if (mode === 'edit' && expense) {
      setForm({
        expense_date: expense.expense_date || '',
        vendor_name: expense.vendor_name || '',
        expense_type: expense.expense_type || 'materials',
        work_category: expense.work_category || '',
        description: expense.description || '',
        reference_number: expense.reference_number || '',
        project_id: expense.project_id || '',
        subtotal: centsToDollarInput(expense.subtotal_cents),
        tax: centsToDollarInput(expense.tax_cents),
        other_fees: centsToDollarInput(expense.other_fees_cents),
        total: centsToDollarInput(expense.total_cents),
        payment_method: expense.payment_method || '',
        payer_key: expense.payer_id ? `${expense.payer_type}:${expense.payer_id}` : expense.payer_type || 'company',
        reimbursement_status: expense.reimbursement_status || 'not_applicable',
        internal_notes: expense.internal_notes || '',
      });
      loadAttached();
    } else {
      setForm({
        ...EMPTY,
        expense_date: new Date().toISOString().slice(0, 10),
        project_id: locked ? lockedProjectId : '',
      });
      setAttached([]);
    }
  }, [open, mode, expense, locked, lockedProjectId, loadAttached]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const suggestedTotal = () => {
    const s = dollarsToCents(form.subtotal) || 0;
    const t = dollarsToCents(form.tax) || 0;
    const o = dollarsToCents(form.other_fees) || 0;
    return s + t + o;
  };

  const buildPayload = () => {
    const [payer_type, payer_id] = form.payer_key.includes(':')
      ? form.payer_key.split(':')
      : [form.payer_key, null];
    return {
      expense_date: form.expense_date,
      vendor_name: form.vendor_name || null,
      expense_type: form.expense_type,
      work_category: form.work_category || null,
      description: form.description || null,
      reference_number: form.reference_number || null,
      project_id: form.project_id || null,
      subtotal_cents: dollarsToCents(form.subtotal),
      tax_cents: dollarsToCents(form.tax),
      other_fees_cents: dollarsToCents(form.other_fees),
      total_cents: dollarsToCents(form.total),
      payment_method: form.payment_method || null,
      payer_type,
      payer_id,
      reimbursement_status: form.reimbursement_status,
      internal_notes: form.internal_notes || null,
    };
  };

  const validate = () => {
    if (!form.expense_date) { toast.error('Expense date is required'); return false; }
    const total = dollarsToCents(form.total);
    if (total == null) { toast.error('Total amount is required'); return false; }
    if (total < 0) { toast.error('Total cannot be negative'); return false; }
    return true;
  };

  const handleSubmit = async () => {
    if (submitting || !validate()) return;
    setSubmitting(true);
    try {
      const payload = buildPayload();
      const res = mode === 'edit'
        ? await expensesApi.update(expense.id, payload)
        : await expensesApi.create(payload);
      toast.success(mode === 'edit' ? 'Expense updated' : 'Expense saved');
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save expense');
    } finally {
      setSubmitting(false);
    }
  };

  const doArchiveToggle = async () => {
    try {
      const res = expense.is_archived
        ? await expensesApi.restore(expense.id)
        : await expensesApi.archive(expense.id);
      toast.success(expense.is_archived ? 'Expense restored' : 'Expense archived');
      onOpenChange(false);
      onSaved?.(res.data);
    } catch { toast.error('Action failed'); }
  };

  const employees = people.filter((p) => p.assignee_type === 'employee');
  const subs = people.filter((p) => p.assignee_type === 'subcontractor');
  const archivedExpense = mode === 'edit' && expense?.is_archived;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#111111] border-gray-800 text-white w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="expense-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            {mode === 'edit' ? 'Expense Details' : 'New Expense'}
            {archivedExpense && (
              <span className="text-[10px] uppercase text-gray-400 border border-gray-600 rounded px-1.5 py-0.5">Archived</span>
            )}
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            Date, type, and total are required. Receipts are uploaded inside the project's Financials tab.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <Section title="Expense">
            <Field label="Expense Date *">
              <Input type="date" value={form.expense_date} onChange={(e) => set('expense_date', e.target.value)} className={inputCls} data-testid="xf-date" />
            </Field>
            <Field label="Vendor">
              <Input value={form.vendor_name} onChange={(e) => set('vendor_name', e.target.value)} placeholder="e.g., Home Depot" className={inputCls} data-testid="xf-vendor" />
            </Field>
            <Field label="Expense Type *">
              <select value={form.expense_type} onChange={(e) => set('expense_type', e.target.value)} className={selectCls} data-testid="xf-type">
                {EXPENSE_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Work Category" help="e.g., Flooring, Electrical, Demo">
              <Input value={form.work_category} onChange={(e) => set('work_category', e.target.value)} className={inputCls} data-testid="xf-category" />
            </Field>
            <Field label="Description" full>
              <Input value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="e.g., LVP installation materials" className={inputCls} data-testid="xf-description" />
            </Field>
            <Field label="Reference Number" help="Invoice / order number">
              <Input value={form.reference_number} onChange={(e) => set('reference_number', e.target.value)} className={inputCls} data-testid="xf-reference" />
            </Field>
          </Section>

          <Section title="Project">
            <Field label="Project" full>
              <select value={form.project_id} onChange={(e) => set('project_id', e.target.value)} className={selectCls} disabled={locked} data-testid="xf-project">
                <option value="">General BuildBroz Expense</option>
                {locked && !projects.some((p) => p.id === lockedProjectId) ? (
                  <option value={lockedProjectId}>Current Project</option>
                ) : null}
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
          </Section>

          <Section title="Amount">
            <Field label="Subtotal ($)">
              <MoneyInput value={form.subtotal} onChange={(v) => set('subtotal', v)} className={inputCls} data-testid="xf-subtotal" />
            </Field>
            <Field label="Tax ($)">
              <MoneyInput value={form.tax} onChange={(v) => set('tax', v)} className={inputCls} data-testid="xf-tax" />
            </Field>
            <Field label="Other Fees ($)">
              <MoneyInput value={form.other_fees} onChange={(v) => set('other_fees', v)} className={inputCls} data-testid="xf-fees" />
            </Field>
            <Field label="Total ($) *" help="The real receipt total is authoritative.">
              <div className="flex gap-2">
                <MoneyInput value={form.total} onChange={(v) => set('total', v)} className={inputCls} data-testid="xf-total" />
                {suggestedTotal() > 0 && dollarsToCents(form.total) !== suggestedTotal() && (
                  <Button type="button" variant="outline" className="border-gray-700 text-gray-400 text-xs px-2 shrink-0"
                    onClick={() => set('total', String(suggestedTotal() / 100))} data-testid="xf-use-suggested">
                    Use ${(suggestedTotal() / 100).toFixed(2)}
                  </Button>
                )}
              </div>
            </Field>
          </Section>

          <Section title="Payment">
            <Field label="Payment Method">
              <select value={form.payment_method} onChange={(e) => set('payment_method', e.target.value)} className={selectCls} data-testid="xf-payment-method">
                <option value="">Not set</option>
                {PAYMENT_METHOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Paid By">
              <select value={form.payer_key} onChange={(e) => set('payer_key', e.target.value)} className={selectCls} data-testid="xf-payer">
                <option value="company">BuildBroz / Company</option>
                <option value="other">Other</option>
                {employees.length > 0 && (
                  <optgroup label="Employees">
                    {employees.map((p) => <option key={p.assignee_id} value={`employee:${p.assignee_id}`}>{p.display_name}</option>)}
                  </optgroup>
                )}
                {subs.length > 0 && (
                  <optgroup label="Subcontractors">
                    {subs.map((p) => <option key={p.assignee_id} value={`subcontractor:${p.assignee_id}`}>{p.display_name}</option>)}
                  </optgroup>
                )}
              </select>
            </Field>
            <Field label="Reimbursement">
              <select value={form.reimbursement_status} onChange={(e) => set('reimbursement_status', e.target.value)} className={selectCls} data-testid="xf-reimbursement">
                {REIMBURSEMENT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
          </Section>

          {mode === 'edit' && attached.length > 0 && (
            <Section title="Linked Receipt">
              <div className="md:col-span-2 space-y-3">
                {attached.map((r) => (
                  <div key={r.id} className="flex items-center gap-3 bg-[#1A1A1A] border border-gray-800 rounded-md p-2" data-testid={`xf-receipt-${r.id}`}>
                    <ReceiptThumb receipt={r} className="w-12 h-12 rounded shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-white truncate">{r.original_filename}</div>
                      <div className="text-xs text-gray-500">{fmtDate(r.created_at?.slice(0, 10))} · {fmtBytes(r.size_bytes)}</div>
                    </div>
                    <button onClick={() => setPreview(r)} className="text-gray-500 hover:text-[#D4AF37] p-1" title="Preview"><Eye size={15} /></button>
                    <button onClick={() => downloadReceipt(r)} className="text-gray-500 hover:text-[#D4AF37] p-1" title="Download"><Download size={15} /></button>
                  </div>
                ))}
                <p className="text-xs text-gray-600">Manage receipts (edit / delete) from the project's Financials tab.</p>
              </div>
            </Section>
          )}

          <Section title="Internal">
            <Field label="Internal Notes" full help="Internal only">
              <Textarea value={form.internal_notes} onChange={(e) => set('internal_notes', e.target.value)} rows={2} className={inputCls} data-testid="xf-notes" />
            </Field>
          </Section>
        </div>

        <div className="flex flex-wrap gap-3 justify-between border-t border-gray-800 pt-4 sticky bottom-0 bg-[#111111]">
          <div>
            {mode === 'edit' && expense && (
              <Button onClick={doArchiveToggle} variant="outline" className="border-gray-700 text-gray-300" data-testid="xf-archive-toggle">
                {expense.is_archived ? <><RotateCcw size={15} /> Restore</> : <><Archive size={15} /> Archive</>}
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} className="border-gray-700 text-gray-300" data-testid="xf-cancel">Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="xf-submit">
              {submitting && <Loader2 size={16} className="animate-spin" />}
              {mode === 'edit' ? 'Save Changes' : 'Save Expense'}
            </Button>
          </div>
        </div>

        <ReceiptPreviewDialog open={Boolean(preview)} onOpenChange={(o) => !o && setPreview(null)} receipt={preview} />
      </DialogContent>
    </Dialog>
  );
};
